import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, KeyRound, Loader2, X } from "lucide-react";
import { StaffActivity } from "@/components/admin/StaffActivity";

function SectionPicks({ meta, team, extra, onChange }) {
    const base = meta.teams[team]?.sections || [];
    return (
        <div className="flex flex-wrap gap-1.5">
            {Object.entries(meta.sections).map(([k, l]) => {
                const inBase = base.includes(k);
                const on = inBase || extra.includes(k);
                return (
                    <button key={k} type="button" disabled={inBase} data-testid={`staff-section-${k}`} onClick={() => onChange(on ? extra.filter((x) => x !== k) : [...extra, k])}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors ${inBase ? "border-navy bg-navy text-white" : on ? "border-orange bg-orange text-white" : "border-navy/15 bg-white text-navy/60"}`}>{l}</button>
                );
            })}
        </div>
    );
}

function TempPassword({ data, onClose }) {
    return (
        <div data-testid="staff-temp-password" className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900">
            <div className="flex items-start justify-between"><p className="font-bold">Share these sign-in details with {data.name} privately</p><button aria-label="Close" onClick={onClose}><X size={16} /></button></div>
            <p className="mt-1">Email: <b>{data.email}</b> · Temporary password: <b className="font-mono">{data.temp_password}</b></p>
            <p className="mt-1 text-xs">This password is shown only once. They must set their own password on first sign-in.</p>
            <button onClick={() => { navigator.clipboard?.writeText(`HOMIIE admin login\nEmail: ${data.email}\nTemporary password: ${data.temp_password}`); toast.success("Copied"); }} className="mt-2 flex items-center gap-1 rounded-full bg-emerald-700 px-3 py-1.5 text-xs font-bold text-white"><Copy size={12} /> Copy details</button>
        </div>
    );
}

function NewStaff({ meta, onCreated }) {
    const [f, setF] = useState({ name: "", email: "", phone: "", team: "accounts", extra_sections: [] });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try { const { data } = await api.post("/admin/staff", f); onCreated(data); setF({ name: "", email: "", phone: "", team: "accounts", extra_sections: [] }); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const cls = "rounded-xl border border-navy/15 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange";
    return (
        <div data-testid="new-staff-form" className="space-y-3 rounded-2xl border border-orange/30 bg-orange/5 p-4">
            <div className="grid gap-2 sm:grid-cols-4">
                <input data-testid="staff-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className={cls} />
                <input data-testid="staff-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Work email" className={cls} />
                <input data-testid="staff-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Mobile (optional)" className={cls} />
                <select data-testid="staff-team" value={f.team} onChange={(e) => setF({ ...f, team: e.target.value, extra_sections: [] })} className={cls}>{Object.entries(meta.teams).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}</select>
            </div>
            <p className="text-xs font-semibold text-navy/55">Sections (dark = team default, tap others to add)</p>
            <SectionPicks meta={meta} team={f.team} extra={f.extra_sections} onChange={(x) => setF({ ...f, extra_sections: x })} />
            <button data-testid="staff-create-btn" disabled={busy} onClick={submit} className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2.5 text-xs font-bold text-white disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create staff login</button>
        </div>
    );
}

function StaffRow({ s, meta, onChanged, onTemp }) {
    const [extra, setExtra] = useState(s.extra_sections || []);
    const patch = async (body, ok) => {
        try { await api.patch(`/admin/staff/${s.id}`, body); toast.success(ok); onChanged(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    const reset = async () => {
        if (!window.confirm(`Reset ${s.name}'s password?`)) return;
        try { const { data } = await api.post(`/admin/staff/${s.id}/reset-password`); onTemp({ ...s, temp_password: data.temp_password }); onChanged(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    return (
        <div data-testid={`staff-row-${s.email}`} className={`space-y-2 rounded-2xl border bg-white p-4 ${s.active === false ? "border-red-200 opacity-70" : "border-navy/10"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div><p className="font-heading font-bold text-navy">{s.name} <span className="ml-1 text-xs font-semibold text-navy/45">{s.email}</span></p>
                    <p className="text-[11px] text-navy/45">{s.force_password_reset ? "Hasn't set own password yet" : "Active login"}{s.active === false ? " · ACCESS OFF" : ""}</p></div>
                <div className="flex items-center gap-2">
                    <select data-testid={`staff-team-${s.email}`} value={s.team} onChange={(e) => patch({ team: e.target.value, extra_sections: [] }, "Team updated")} className="rounded-full border border-navy/15 bg-white px-3 py-1.5 text-xs font-bold text-navy">{Object.entries(meta.teams).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}</select>
                    <button data-testid={`staff-reset-${s.email}`} onClick={reset} className="flex items-center gap-1 rounded-full border border-navy/15 px-3 py-1.5 text-xs font-bold text-navy"><KeyRound size={12} /> Reset password</button>
                    <button data-testid={`staff-toggle-${s.email}`} onClick={() => patch({ active: s.active === false }, s.active === false ? "Access turned on" : "Access turned off")} className={`rounded-full px-3 py-1.5 text-xs font-bold text-white ${s.active === false ? "bg-emerald-600" : "bg-red-500"}`}>{s.active === false ? "Turn on" : "Turn off"}</button>
                </div>
            </div>
            <SectionPicks meta={meta} team={s.team} extra={extra} onChange={setExtra} />
            {JSON.stringify(extra) !== JSON.stringify(s.extra_sections || []) && <button data-testid={`staff-save-sections-${s.email}`} onClick={() => patch({ extra_sections: extra }, "Access updated")} className="rounded-full bg-orange px-3 py-1.5 text-xs font-bold text-white">Save access</button>}
        </div>
    );
}

export default function AdminTeam() {
    const [meta, setMeta] = useState(null);
    const [rows, setRows] = useState([]);
    const [temp, setTemp] = useState(null);
    const [tab, setTab] = useState("people");
    const load = useCallback(() => {
        api.get("/admin/staff").then((r) => setRows(r.data)).catch(() => {});
    }, []);
    useEffect(() => { api.get("/admin/staff/meta").then((r) => setMeta(r.data)).catch(() => {}); load(); }, [load]);
    if (!meta) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>;
    return (
        <div className="space-y-4">
            <div><h1 className="font-heading text-2xl font-extrabold text-navy">Team & Access</h1>
                <p className="text-sm text-navy/55">Create staff logins and choose which sections each person can open. Only the Master admin sees this page. Access is enforced on the server too.</p></div>
            <div className="inline-flex rounded-full bg-navy/5 p-1">
                {[["people", "Staff & access"], ["activity", "Activity"]].map(([k, l]) => <button key={k} data-testid={`team-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${tab === k ? "bg-orange text-white" : "text-navy/60"}`}>{l}</button>)}
            </div>
            {tab === "activity" ? <StaffActivity staff={rows} /> : <>
            {temp && <TempPassword data={temp} onClose={() => setTemp(null)} />}
            <NewStaff meta={meta} onCreated={(d) => { setTemp(d); load(); }} />
            <div className="space-y-3">{rows.map((s) => <StaffRow key={`${s.id}-${s.team}-${(s.extra_sections || []).join()}`} s={s} meta={meta} onChanged={load} onTemp={setTemp} />)}</div>
            {rows.length === 0 && <p className="text-sm text-navy/45">No staff yet.</p>}
            </>}
        </div>
    );
}
